import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Route, RouteComponentProps } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  IonPage,
  IonRouterOutlet,
  isPlatform,
  useIonRouter,
  useIonViewDidLeave,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { App } from '@capacitor/app';
import {
  AppHeader_v2 as AppHeader,
  Loading,
  OnBoardingProgressFeature,
  OnboardingProgressTemplate,
  OnBoardingScreenOptions,
  OnBoardingScreenStyleOptions,
  OnboardingVariant,
} from '@mindly/ui-components';
import { FlowScreenPage } from './pages';
import { fetchNewDataOnBoardingFlow, fetchOnBoardingFlow } from '../../redux/onBoardingFlow/thunk';
import {
  experimentVariantSelector,
  flowCurrentScreenDataSelector,
  flowErrorSelector,
  flowIsTestingSelector,
  flowPrevScreenIdSelector,
  flowProgressSelector,
  flowUserEmailSelector,
  getGoalScreenDataSelector,
  isLoadingFlowSelector,
  onBoardingFlowSelector,
} from '../../redux/onBoardingFlow/selector';
import useToast from '../../context/ToastContext/ToastContext';
import { PublicRoutes } from '../../routes/navigation';
import {
  isTestingMode,
  setGoalScreenData,
  setInitialFlowData,
  setProgress,
  setSelectedExperimentVariant,
} from '../../redux/onBoardingFlow/reducer';
import { setSupportButtonActive } from '../../redux/supportButton/reducer';
import OnBoardingRedirect from '../OnBoarding/pages/OnBoardingRedirect';
import { FunnelTypeEnum, pageAnimation, useAnalytics, useFunnelUserIdentity } from '../../shared';
import useLangs from '../../context/LangsContext';
import { PromoCodeTimerFeature } from 'src/features/PromoCodeTimerFeature';
import { useFlowPromocode } from 'src/shared/hooks/useFlowPromocode';
import { ONBOARDING_SCREEN_OPTIONS_WITHOUT_PROGRESS_BAR } from './constants';
import { animateGoalScreen } from './animateGoalScreen';
import { selectUnUsedPromoCode } from 'src/redux/promoCodes/promoCodes.selectors';

const OnBoardingFlowPage: FC<RouteComponentProps<{ flowId: string; isTesting?: string }>> = ({ match, location }) => {
  const { flowId } = match.params;
  const { search } = location;
  const searchParams = new URLSearchParams(search);
  const iTestMode = searchParams.get('iTestMode');
  const testingVatiantId = searchParams.get('testingVariantId');

  const [isInit, setIsInit] = useState(false);

  const dispatch = useDispatch();
  const { showToast } = useToast();
  const navigation = useIonRouter();
  const { changeLocale } = useLangs();

  const flowData = useSelector(onBoardingFlowSelector);
  const flowError = useSelector(flowErrorSelector);
  const isLoading = useSelector(isLoadingFlowSelector);
  const { progress, currentCount, totalCount } = useSelector(flowProgressSelector);
  const currentScreenData = useSelector(flowCurrentScreenDataSelector);
  const prevScreenId = useSelector(flowPrevScreenIdSelector);
  const isTestingModeSelector = useSelector(flowIsTestingSelector);
  const selectedExperimentVariant = useSelector(experimentVariantSelector);
  const { currentGoalScreenCount } = useSelector(getGoalScreenDataSelector);
  const unUsedPromoCode = useSelector(selectUnUsedPromoCode);

  const { isInit: isInitIdentity } = useFunnelUserIdentity(isInit, !!flowData?.id, !!flowData?.isDefault, flowId);
  const { promocodeEndDate, promocodePercent } = useFlowPromocode();
  const flowUserEmail = useSelector(flowUserEmailSelector);

  const { trackPredefinedEvent } = useAnalytics();
  const isExperimentInited = useRef(false);

  useIonViewWillEnter(async () => {
    console.info('[FLOW]: Enter to flowId: ', flowId);
    if (flowData?.id === flowId && !isInit) {
      console.info('[FLOW]: Flow already loaded: ', flowId);
      dispatch(isTestingMode(!!iTestMode));
      await dispatch(fetchNewDataOnBoardingFlow({ id: flowId, isTestMode: !!iTestMode }));
      if (!!iTestMode && testingVatiantId) {
        dispatch(setSelectedExperimentVariant({ name: 'test', id: testingVatiantId }));
      }
      setIsInit(true);
      return;
    }
    if (!isInit) {
      await dispatch(fetchOnBoardingFlow({ id: flowId, isTestMode: !!iTestMode }));
    }

    console.info('[FLOW]: Flow data loaded: ', flowId);
    dispatch(isTestingMode(!!iTestMode));
    if (!!iTestMode && testingVatiantId) {
      dispatch(setSelectedExperimentVariant({ name: 'test', id: testingVatiantId }));
    }
    setIsInit(true);
  }, [flowId, iTestMode, flowData, isInit, testingVatiantId]);

  useIonViewWillEnter(() => {
    dispatch(setSupportButtonActive(false));
  });

  useIonViewDidLeave(() => {
    console.info('[FLOW]: Leave flowId: ', flowId);
    setIsInit(false);
  });

  useIonViewWillLeave(() => {
    dispatch(setInitialFlowData());
  });

  useEffect(() => {
    if (!flowData) return;
    console.info('[FLOW]: Data flowId changed: ', flowData.id);
    changeLocale(flowData.locale);
  }, [flowData]);

  useEffect(() => {
    if (!flowError) return;
    showToast({ message: flowError });
    console.error(`[FLOW]: FlowId ${flowId} error: `, flowError);
  }, [flowError, flowId]);

  //Track experiment started
  useEffect(() => {
    if (!isInit || !isInitIdentity) return;
    if (!flowData) return;
    if (isTestingModeSelector) return;
    if (flowData?.experiment?.enabled !== true || !selectedExperimentVariant?.name) {
      return;
    }

    if (!isExperimentInited.current) {
      isExperimentInited.current = true;
      trackPredefinedEvent('EXPERIMENT_STARTED', {
        'Experiment name': flowData.experiment?.name ?? '',
        'Variant name': selectedExperimentVariant.name,
      });
    }
  }, [isInit, isInitIdentity, flowData, isExperimentInited, selectedExperimentVariant, isTestingModeSelector]);

  useEffect(() => {
    if (!isInit || !isInitIdentity) return;
    if (!currentScreenData || !flowId) return;
    if (isTestingModeSelector) return;

    const eventData = {
      funnel_id: flowId,
      screen_id: currentScreenData.id,
      funnel_name: flowData?.name,
      funnel_type: flowData?.isDefault
        ? FunnelTypeEnum.DEFAULT
        : flowData?.isWebToWeb
          ? FunnelTypeEnum.WEB_TO_WEB
          : FunnelTypeEnum.WEB_TO_APP,
      screen_name: currentScreenData?.name ?? '',
    };

    if (currentScreenData.type === OnBoardingScreenOptions.START || currentScreenData.isStartScreen) {
      trackPredefinedEvent('FUNNEL_STARTED', {
        ...eventData,
        locale: flowData?.locale,
      });
    } else if (currentScreenData.type === OnBoardingScreenOptions.EMAIL) {
      trackPredefinedEvent('FUNNEL_REGISTRATION_EMAIL_SCREEN_VIEWED', {
        ...eventData,
        screen_name: undefined,
      });
    } else if (currentScreenData.type === OnBoardingScreenOptions.CONFIRM) {
      trackPredefinedEvent('FUNNEL_REGISTRATION_CONFIRMATION_SCREEN_VIEWED', {
        ...eventData,
        screen_name: undefined,
      });
    } else if (currentScreenData.type === OnBoardingScreenOptions.PAYWALL) {
      trackPredefinedEvent('FUNNEL_PAYWALL_SCREEN_VIEWED', {
        ...eventData,
        screen_name: undefined,
        user_email: flowUserEmail ?? '',
      });
    } else {
      trackPredefinedEvent('FUNNEL_SCREEN_VIEWED', {
        ...eventData,
        screen_order: currentScreenData.order,
      });
    }
  }, [currentScreenData, isInit, flowId, isTestingModeSelector]);

  const updateGoalScreenData = () => {
    const nextGoalScreenCount = currentGoalScreenCount - 1;

    dispatch(
      setProgress({
        screenId: currentScreenData?.id ?? '',
        currentGoalScreenCount: nextGoalScreenCount,
      })
    );

    dispatch(
      setGoalScreenData({
        currentGoalScreenCount: currentGoalScreenCount - 1,
      })
    );
  };

  const handleBackButton = async () => {
    if (currentScreenData?.type === OnBoardingScreenOptions.GOAL && currentGoalScreenCount > 0) {
      animateGoalScreen({
        updateGoalScreenData,
        direction: 'back',
      });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (navigation.canGoBack()) {
      navigation.goBack((baseEl, opts) => pageAnimation(baseEl, opts, true));
    } else if (prevScreenId) {
      navigation.routeInfo.routeDirection = 'back';
      if (!flowData?.isDefault) {
        const prevURL =
          generatePath(PublicRoutes.ON_BOARDING_FLOW_SCREEN, {
            screenId: prevScreenId,
            flowId,
          }) + `?${params.toString()}`;
        navigation.push(prevURL, 'back', 'pop', undefined, (baseEl, opts) => pageAnimation(baseEl, opts, true));
      } else {
        navigation.push(
          generatePath(PublicRoutes.ON_BOARDING_FLOW_SCREEN, { screenId: prevScreenId, flowId }) +
            `?${params.toString()}`,
          'back',
          'pop',
          undefined,
          (baseEl, opts) => pageAnimation(baseEl, opts, true)
        );
      }
    } else {
      navigation.routeInfo.routeDirection = 'back';
      if (!flowData?.isDefault) {
        const prevURL = generatePath(PublicRoutes.ON_BOARDING_FLOW, { flowId }) + `?${params.toString()}`;
        navigation.push(prevURL, 'back', 'pop', undefined, (baseEl, opts) => pageAnimation(baseEl, opts, true));
      } else {
        navigation.push(
          generatePath(PublicRoutes.ON_BOARDING_FLOW, { flowId }) + `?${params.toString()}`,
          'back',
          'pop',
          undefined,
          (baseEl, opts) => pageAnimation(baseEl, opts, true)
        );
      }
    }
  };

  const promocodeTimer = useMemo(
    () => (
      <PromoCodeTimerFeature className="!mb-3" endDate={promocodeEndDate} percentOff={promocodePercent} expandWidth />
    ),
    [promocodeEndDate, promocodePercent]
  );

  const isPromocodeTimerVisible = useMemo(() => {
    if ((promocodeEndDate && promocodePercent) || unUsedPromoCode) return true;

    return false;
  }, [unUsedPromoCode, promocodeEndDate]);

  useEffect(() => {
    if (isPlatform('hybrid') && isPlatform('android')) {
      App.addListener('backButton', handleBackButton);

      return () => {
        App.removeAllListeners();
      };
    }
  }, []);

  const isProgressBarVisible = useMemo(() => {
    const isProgressVisibleForCurrentScreen =
      typeof currentScreenData?.isProgressVisible === 'boolean' ? currentScreenData?.isProgressVisible : true;

    if (flowData?.progress?.template === OnboardingProgressTemplate.counter) {
      return (
        currentScreenData && !navigation.routeInfo.pathname.includes('redirect') && isProgressVisibleForCurrentScreen
      );
    }

    if (
      currentScreenData &&
      currentScreenData.type === OnBoardingScreenOptions.GOAL &&
      currentScreenData.isStartScreen
    ) {
      return currentGoalScreenCount > 0 && isProgressVisibleForCurrentScreen;
    }

    return (
      currentScreenData &&
      !ONBOARDING_SCREEN_OPTIONS_WITHOUT_PROGRESS_BAR.includes(currentScreenData.type) &&
      !currentScreenData.isStartScreen &&
      !navigation.routeInfo.pathname.includes('redirect') &&
      isProgressVisibleForCurrentScreen
    );
  }, [flowData?.progress?.template, currentScreenData, currentGoalScreenCount, navigation.routeInfo.pathname]);

  const isBackButtonDisabled = useMemo(() => {
    if (flowData?.progress?.template === OnboardingProgressTemplate.counter) {
      return false;
    }

    return (
      currentScreenData?.type &&
      [OnBoardingScreenOptions.PAYWALL, OnBoardingScreenOptions.CONFIRM, OnBoardingScreenOptions.EMAIL].includes(
        currentScreenData.type
      )
    );
  }, [flowData?.progress?.template, currentScreenData]);

  return (
    <IonPage>
      <Loading isOpen={isLoading} />
      {isProgressBarVisible ? (
        <OnBoardingProgressFeature
          progress={progress}
          isBackButtonDisabled={isBackButtonDisabled}
          contentStyle={
            currentScreenData && 'contentStyle' in currentScreenData && currentScreenData.contentStyle
              ? currentScreenData.contentStyle
              : OnBoardingScreenStyleOptions.DARK
          }
          onBackButtonClick={handleBackButton}
          startAdornment={isPromocodeTimerVisible ? promocodeTimer : undefined}
          theme={{
            ...flowData?.theme_v2,
            template: flowData?.theme_v2?.template ?? flowData?.theme ?? OnboardingVariant.default,
          }}
          template={flowData?.progress?.template}
          image={flowData?.progress?.image}
          totalCount={totalCount}
          currentCount={currentCount}
          progressSettings={currentScreenData?.progressSettings}
        />
      ) : (
        // !navigation.routeInfo.pathname.includes('redirect') &&
        !!promocodeEndDate &&
        currentScreenData &&
        ![OnBoardingScreenOptions.PAYWALL].includes(currentScreenData?.type) && (
          <AppHeader className="[&_ion-toolbar]:[--background:transparent]">{promocodeTimer}</AppHeader>
        )
      )}
      <IonRouterOutlet animated={true} animation={pageAnimation} mode={'ios'}>
        <Route exact path={`${match.url}/r/redirect`} component={OnBoardingRedirect} />
        <Route exact path={`${match.url}/:screenId?`} component={FlowScreenPage} />
      </IonRouterOutlet>
    </IonPage>
  );
};

export default memo(OnBoardingFlowPage);
